import React, { useMemo } from 'react';
import Decimal from 'decimal.js';
import { OrderBook as OrderBookType, OrderBookLevel, TokenInfo } from '../types';
import { formatPrice, formatQuantity, formatPercentage } from '../utils/formatting';
import './OrderBook.css';

interface OrderBookProps {
    orderBook: OrderBookType;
    asset: TokenInfo;
}

interface OrderBookSideProps {
    levels: OrderBookLevel[];
    side: 'bids' | 'asks';
    assetSymbol: string;
    midPrice: Decimal;
}

const calculatePercentageFromMid = (levelPrice: Decimal, midPrice: Decimal): number => {
    if (midPrice.lte(0)) return 0;
    return levelPrice.sub(midPrice).div(midPrice).mul(100).toNumber();
};

// Memoized individual order level component
const OrderLevel = React.memo<{
    level: OrderBookLevel;
    index: number;
    isBids: boolean;
    levelSizePercent: number;
    cumulativeQty: Decimal;
    cumulativeSizePercent: number;
    percentFromMid: number;
    midPrice: Decimal;
}>(({ level, index, isBids, levelSizePercent, cumulativeQty, cumulativeSizePercent, percentFromMid, midPrice }) => {
    const percentDisplay = useMemo(() => formatPercentage(percentFromMid), [percentFromMid]);

    // Use mid-price as reference for consistent precision
    const formattedPrice = useMemo(() => formatPrice(level.price, midPrice), [level.price, midPrice]);
    const formattedQuantity = useMemo(() => formatQuantity(level.quantity), [level.quantity]);
    const formattedCumulativeQty = useMemo(() => formatQuantity(cumulativeQty), [cumulativeQty]);

    return (
        <div
            key={index}
            className={`order-level ${isBids ? 'bid' : 'ask'}`}
        >
            <div className="level-background">
                {/* Transparent cumulative bar */}
                <div
                    className={`cumulative-bar ${isBids ? 'bid' : 'ask'}`}
                    style={{ width: `${cumulativeSizePercent}%` }}
                />
                {/* Solid level size bar */}
                <div
                    className={`level-bar ${isBids ? 'bid' : 'ask'}`}
                    style={{ width: `${levelSizePercent}%` }}
                />
            </div>

            <div className="level-content">
                <span className="price">{formattedPrice}</span>
                <span className="quantity">{formattedQuantity}</span>
                <span className="total">{formattedCumulativeQty}</span>
                <span className={`percent-from-mid ${percentFromMid >= 0 ? 'positive' : 'negative'}`}>
                    {percentDisplay}
                </span>
            </div>
        </div>
    );
});

const OrderBookSide = React.memo<OrderBookSideProps>(({ levels, side, assetSymbol, midPrice }) => {
    const isBids = side === 'bids';

    // Memoize expensive calculations
    const { cumulativeQuantities, maxCumulative } = useMemo(() => {
        const cumulativeQuantities: Decimal[] = [];
        let runningTotal = new Decimal(0);

	if (isBids) {
            levels.forEach((level, index) => {
                runningTotal = runningTotal.add(level.quantity);
                cumulativeQuantities[index] = runningTotal;
	    });
	} else {
            for (let i = levels.length - 1; i >= 0; i--) {
                runningTotal = runningTotal.add(levels[i].quantity);
                cumulativeQuantities[i] = runningTotal;
	    }
	}

        return { cumulativeQuantities, maxCumulative: runningTotal };
    }, [isBids, levels]);

    // Memoize level calculations
    const levelData = useMemo(() => {
        return levels.map((level, index) => {
            const cumulativeQty = cumulativeQuantities[index];
            const levelSizePercent = level.quantity.div(maxCumulative).mul(100).toNumber();
            const cumulativeSizePercent = cumulativeQty.div(maxCumulative).mul(100).toNumber();
            const percentFromMid = calculatePercentageFromMid(level.price, midPrice);

            return {
                level,
                index,
                levelSizePercent,
                cumulativeQty,
                cumulativeSizePercent,
                percentFromMid,
            };
        });
    }, [levels, cumulativeQuantities, maxCumulative, midPrice]);

    return (
        <div className={`order-book-side ${side}`}>
            <div className="side-header">
                <h3>{isBids ? 'Bids' : 'Asks'}</h3>
                <div className="column-headers">
                    <span>Price</span>
                    <span className="quantity">Size ({assetSymbol})</span>
                    <span className="total">Total ({assetSymbol})</span>
                    <span className="percent-from-mid">vs Mid (%)</span>
                </div>
            </div>

            <div className="orders-list">
                {levelData.map((data) => (
                    <OrderLevel
                        key={data.index}
                        level={data.level}
                        index={data.index}
                        isBids={isBids}
                        levelSizePercent={data.levelSizePercent}
                        cumulativeQty={data.cumulativeQty}
                        cumulativeSizePercent={data.cumulativeSizePercent}
                        percentFromMid={data.percentFromMid}
                        midPrice={midPrice}
                    />
                ))}
            </div>
        </div>
    );
});

const OrderBook = React.memo<OrderBookProps>(({ orderBook, asset }) => {
    // Memoize spread calculation
    const spreadPercent = useMemo(() => {
        return orderBook.midPrice.gt(0)
            ? orderBook.spread.div(orderBook.midPrice).mul(100).toNumber()
            : 0;
    }, [orderBook.midPrice, orderBook.spread]);

    // Memoize processed levels to avoid slicing/reversing on every render
    const { processedAsks, processedBids } = useMemo(() => {
        return {
            processedAsks: orderBook.asks.slice(0, 10).reverse(),
            processedBids: orderBook.bids.slice(0, 10),
        };
    }, [orderBook.asks, orderBook.bids]);

    // Memoize formatted prices using mid-price as reference
    const formattedMidPrice = useMemo(() => formatPrice(orderBook.midPrice), [orderBook.midPrice]);
    const formattedSpread = useMemo(() => formatPrice(orderBook.spread, orderBook.midPrice), [orderBook.spread, orderBook.midPrice]);

    return (
        <div className="order-book">
            <div className="order-book-header">
                <h2>Order Book: {asset.symbol}</h2>
                <div className="asset-info">
                    <div className="market-info">
                        <div className="spread-info">
                            <span className="label">Spread:</span>
                            <span className="value">
                                {formattedSpread} ({formatPercentage(spreadPercent)})
                            </span>
                        </div>
                        <div className="mid-price-info">
                            <span className="label">Mid Price:</span>
                            <span className="value">{formattedMidPrice}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="order-book-content">
                <OrderBookSide
                    levels={processedAsks}
                    side="asks"
                    assetSymbol={asset.symbol}
                    midPrice={orderBook.midPrice}
                />

                <div className="spread-indicator">
                    <div className="spread-line">
                        Spread: {formattedSpread} ({formatPercentage(spreadPercent)})
                    </div>
                </div>

                <OrderBookSide
                    levels={processedBids}
                    side="bids"
                    assetSymbol={asset.symbol}
                    midPrice={orderBook.midPrice}
                />
            </div>
        </div>
    );
});

function OrderBookOrLoading({orderBook, asset}: {orderBook: OrderBookType | null, asset: TokenInfo}) {
  return orderBook 
    ? <OrderBook orderBook={orderBook} asset={asset}/> 
    : (
      <div className="loading-placeholder">
        <h3>Loading Order Book...</h3>
      </div>
    );
}

export default OrderBookOrLoading; 
